import React, { useEffect, useState } from 'react';
import {
  Modal,
  Pressable,
  StyleSheet,
  Text,
  View,
} from 'react-native';

import { GlassSurface } from '../ui/glass';
import { GlassButton } from '../ui/GlassButton';
import { colors, radii, spacing, typography } from '../../core/theme/tokens';
import { useAuth } from '../../contexts/AuthContext';
import { authService } from '../../modules/Auth/services/authService';
import { ValueStepper } from '../../modules/Onboarding/components/ValueStepper';

type EditTakeHomeModalProps = {
  visible: boolean;
  initialValue: number;
  onClose: () => void;
  onSaved?: (monthlyIncome: number) => void;
};

const INCOME_STEP = 100;

/** Web centered glass dialog to set monthly take-home on the user profile. */
export function EditTakeHomeModal({
  visible,
  initialValue,
  onClose,
  onSaved,
}: EditTakeHomeModalProps) {
  const { refreshUser } = useAuth();
  const [value, setValue] = useState(Math.max(0, Math.round(initialValue)));
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (visible) {
      setValue(Math.max(0, Math.round(initialValue)));
      setError(null);
      setSaving(false);
    }
  }, [visible, initialValue]);

  const handleSave = async () => {
    if (value <= 0) {
      setError('Enter a take-home amount greater than zero.');
      return;
    }
    setSaving(true);
    setError(null);
    try {
      await authService.updateProfile({ monthlyIncome: value });
      await refreshUser();
      onSaved?.(value);
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not save take-home');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <View style={styles.backdrop}>
        <Pressable
          style={styles.dismissArea}
          onPress={onClose}
          accessibilityLabel="Dismiss take-home editor"
        />
        <View style={styles.panelWrap} pointerEvents="box-none">
          <GlassSurface
            tint="neutral"
            rounded="xl"
            showHairline
            style={styles.panel}
            contentStyle={styles.panelContent}
          >
            <Text style={styles.title}>Monthly take-home</Text>
            <Text style={styles.sub}>After-tax income that lands in your accounts each month.</Text>

            <ValueStepper
              label="Take-home"
              value={value}
              prefix="$"
              min={0}
              onChange={setValue}
              onDecrease={() => setValue((v) => Math.max(0, v - INCOME_STEP))}
              onIncrease={() => setValue((v) => v + INCOME_STEP)}
            />

            {error ? <Text style={styles.error}>{error}</Text> : null}

            <View style={styles.actions}>
              <GlassButton variant="ghost" size="md" onPress={onClose} disabled={saving}>
                Cancel
              </GlassButton>
              <GlassButton
                variant="primary"
                size="md"
                onPress={() => {
                  void handleSave();
                }}
                disabled={saving}
              >
                {saving ? 'Saving…' : 'Save'}
              </GlassButton>
            </View>
          </GlassSurface>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(2, 6, 23, 0.72)',
    justifyContent: 'center',
    alignItems: 'center',
    padding: spacing.lg,
  },
  dismissArea: {
    ...StyleSheet.absoluteFillObject,
  },
  panelWrap: {
    width: '100%',
    maxWidth: 420,
    zIndex: 1,
  },
  panel: {
    paddingHorizontal: spacing.lg,
    paddingVertical: spacing.lg,
    borderRadius: radii.xl,
  },
  panelContent: {
    gap: spacing.sm,
  },
  title: {
    ...typography.headline,
    color: colors.text,
  },
  sub: {
    ...typography.body,
    color: colors.textMuted,
    marginBottom: spacing.sm,
  },
  error: {
    ...typography.caption,
    color: colors.warning,
  },
  actions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: spacing.sm,
    marginTop: spacing.sm,
  },
});
